<template>
  <div class="brands-editor">
    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      location="top"
    >
      {{ snackbar.text }}
    </v-snackbar>

    <div class="d-flex justify-space-between align-center mb-4">
      <h2 class="text-h5 mb-0">Brands</h2>
      <v-btn color="primary" @click="showAddDialog"> Add Brand </v-btn>
    </div>

    <!-- Search and filters -->
    <div class="d-flex gap-2 mb-4">
      <v-text-field
        v-model="search"
        label="Search brands"
        density="compact"
        hide-details
        class="flex-grow-1"
        @update:model-value="debouncedSearch"
        append-inner-icon="mdi-magnify"
        clearable
      />
      <v-btn :loading="loading" icon variant="plain" @click="refreshBrands">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </div>

    <!-- Brands Table -->
    <div v-if="initialLoading" class="elevation-1">
      <!-- Skeleton loader for initial load -->
      <v-skeleton-loader
        type="table-heading, table-row-divider, table-row, table-row, table-row, table-row, table-row"
        class="mx-auto"
      ></v-skeleton-loader>
    </div>

    <v-data-table
      v-else
      :page="page"
      :headers="headers"
      :items="brands"
      :loading="loading"
      :items-per-page="pageSize"
      :items-length="totalItems"
      :items-per-page-options="[100]"
      :sort-by="sortBy"
      @update:sort-by="handleSortChange"
      class="elevation-1"
    >
      <!-- Loading overlay for refresh -->
      <template v-if="loading && !initialLoading" #top>
        <v-overlay contained class="align-center justify-center">
          <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
      </template>

      <template #bottom>
        <div class="d-flex justify-space-between align-center px-4">
          <div>
            <div>
              Page {{ page }} of {{ totalPages }} (Total:
              {{ totalItems }} brands)
            </div>
            <div
              v-if="sortBy.length > 0"
              class="text-caption text-medium-emphasis mt-1"
            >
              <v-icon size="small" class="me-1">mdi-sort</v-icon>
              Sorted by
              {{ sortBy[0].key === "brandId" ? "Brand ID" : "Brand Name" }}
              <v-icon size="small">{{
                sortBy[0].order === "desc" ? "mdi-arrow-down" : "mdi-arrow-up"
              }}</v-icon>
            </div>
          </div>
          <v-pagination
            :modelValue="page"
            :length="totalPages"
            :total-visible="7"
            :disabled="loading"
            @update:modelValue="handlePageChange"
            show-first
            show-last
            rounded="circle"
          ></v-pagination>
        </div>
      </template>
      <template #[`item.actions`]="{ item }">
        <v-btn icon variant="plain" size="small" @click="showEditDialog(item)">
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <!-- Add/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="500">
      <v-card>
        <v-card-title>{{
          isEditing ? "Edit Brand" : "Add Brand"
        }}</v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid" @submit.prevent="handleSubmit">
            <v-text-field
              v-if="!isEditing"
              v-model="editedBrand.brandId"
              label="Brand ID"
              :rules="[
                (v) => !!v || 'Brand ID is required',
                (v) =>
                  /^9\d{7}$/.test(v) ||
                  'Brand ID must be an 8-digit number starting with 9',
              ]"
              required
            />
            <v-text-field
              v-model="editedBrand.name"
              label="Brand Name"
              :rules="[(v) => !!v || 'Brand name is required']"
              required
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            :disabled="!valid"
            :loading="saving"
            @click="handleSubmit"
          >
            {{ isEditing ? "Save" : "Add" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from "vue";

// Debounce utility function
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

const headers = [
  { title: "Brand ID", key: "brandId", sortable: true },
  { title: "Brand Name", key: "name", sortable: true },
  { title: "Actions", key: "actions", sortable: false },
];

const brands = ref([]);
const loading = ref(false);
const initialLoading = ref(true); // Track initial load for skeleton
const saving = ref(false);
const search = ref("");
const page = ref(1);
const pageSize = ref(100);
const totalPages = ref(0);
const totalItems = ref(0);
const sortBy = ref([]);
const dialog = ref(false);
const valid = ref(false);
const form = ref(null);
const isEditing = ref(false);
const editedBrand = ref({
  brandId: "",
  name: "",
});

// Snackbar state
const snackbar = ref({
  show: false,
  text: "",
  color: "success",
});

const showNotification = (message, type = "success") => {
  snackbar.value.text = message;
  snackbar.value.color = type;
  snackbar.value.show = true;
};

// API base URL
const API_BASE_URL =
  "https://brands-mini-api-844472556697.europe-west6.run.app";

const fetchPage = async (pageNumber) => {
  if (loading.value) {
    return;
  }
  loading.value = true;
  try {
    // Ensure pageNumber is an integer and within valid range
    const validPageNumber = Math.max(
      1,
      Math.min(Math.floor(pageNumber), totalPages.value || 501)
    );

    // Construct URL with explicit page parameter
    const url = new URL(API_BASE_URL);
    url.searchParams.set("limit", "100");
    url.searchParams.set("page", validPageNumber.toString());

    if (search.value) {
      url.searchParams.set("search", search.value);
    }

    // Add sorting parameters if sorting is active
    if (sortBy.value && sortBy.value.length > 0) {
      const sortItem = sortBy.value[0];
      url.searchParams.set("sortBy", sortItem.key);
      url.searchParams.set("sortOrder", sortItem.order || "asc");
    }

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (!result.data || !Array.isArray(result.data)) {
      throw new Error("Invalid data format received from API");
    }

    // Set the data first
    brands.value = result.data;

    // Then update pagination state
    totalItems.value = result.pagination.totalDocuments;
    totalPages.value = result.pagination.totalPages;
    page.value = result.pagination.currentPage;
  } catch (error) {
    console.error("Error loading brands:", error);
    showNotification("Failed to load brands. Please try again.", "error");
  } finally {
    loading.value = false;
    initialLoading.value = false; // Always turn off initial loading
  }
};

const handleSearch = () => {
  page.value = 1; // Reset to first page when searching
  fetchPage(1); // Use fetchPage directly to preserve sorting
};

// Debounced search function (800ms delay)
const debouncedSearch = debounce(handleSearch, 800);

const handleSortChange = (newSortBy) => {
  sortBy.value = newSortBy;
  page.value = 1; // Reset to first page when sorting changes
  fetchPage(1);
};

const showAddDialog = () => {
  isEditing.value = false;
  editedBrand.value = {
    brandId: "",
    name: "",
  };
  dialog.value = true;
};

const showEditDialog = (item) => {
  isEditing.value = true;
  editedBrand.value = {
    brandId: item.brandId,
    name: item.name,
  };
  dialog.value = true;
};

// No need for a page watcher since we handle page changes in handleTableUpdate

const loadBrands = () => {
  // Initial load or search - always start from page 1
  page.value = 1;
  return fetchPage(1);
};

const refreshBrands = () => {
  // Refresh current page with current sorting
  return fetchPage(page.value);
};

const handlePageChange = async (newPage) => {
  if (!loading.value && newPage !== page.value) {
    await fetchPage(newPage);
  }
};

const handleTableUpdate = async (options) => {
  if (options?.page && !loading.value && options.page !== page.value) {
    await fetchPage(options.page);
  }
};

const handleSubmit = async () => {
  if (!form.value?.validate()) return;

  saving.value = true;
  try {
    const url = `${API_BASE_URL}/?brandId=${editedBrand.value.brandId}`;
    const method = isEditing.value ? "PUT" : "POST";

    const response = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name: editedBrand.value.name,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to save brand");
    }

    dialog.value = false;
    loadBrands(); // Refresh the list
    showNotification(
      `Brand successfully ${isEditing.value ? "updated" : "added"}`,
      "success"
    );
  } catch (error) {
    console.error("Error saving brand:", error);
    showNotification(
      `Failed to ${
        isEditing.value ? "update" : "add"
      } brand. Please try again.`,
      "error"
    );
  } finally {
    saving.value = false;
  }
};

onMounted(() => {
  loadBrands();
});
</script>

<style scoped>
.brands-editor {
  padding: 1rem;
}
</style>
